import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader } from "@learn-me/ui";
import { adminApi } from "@/features/admin/api/adminApi";
import { formatCurrency, formatDate } from "@learn-me/utils";
import {
  Users,
  BookOpen,
  GraduationCap,
  DollarSign,
  TrendingUp,
} from "lucide-react";

export default function AdminDashboardPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-dashboard"],
    queryFn: () => adminApi.getDashboard(),
  });

  const stats = [
    {
      label: "Usuarios Totales",
      value: data?.users.total || 0,
      change: `+${data?.users.newThisMonth || 0} este mes`,
      icon: Users,
      color: "bg-blue-500",
    },
    {
      label: "Cursos Publicados",
      value: `${data?.courses.published || 0}/${data?.courses.total || 0}`,
      change: "publicados",
      icon: BookOpen,
      color: "bg-green-500",
    },
    {
      label: "Inscripciones Activas",
      value: data?.enrollments.active || 0,
      change: `${data?.enrollments.completed || 0} completadas`,
      icon: GraduationCap,
      color: "bg-purple-500",
    },
    {
      label: "Ingresos Totales",
      value: formatCurrency(data?.revenue.total || 0, "USD"),
      change: `${formatCurrency(data?.revenue.thisMonth || 0, "USD")} este mes`,
      icon: DollarSign,
      color: "bg-orange-500",
    },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground">
          Resumen general de la plataforma.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <Card key={stat.label}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg text-white ${stat.color}`}>
                  <stat.icon className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                  <p className="text-2xl font-bold">
                    {isLoading ? "-" : stat.value}
                  </p>
                  <p className="text-xs text-muted-foreground">{stat.change}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Enrollments */}
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">Inscripciones Recientes</h2>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse flex gap-3">
                    <div className="w-10 h-10 bg-muted rounded-full" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-muted rounded w-3/4" />
                      <div className="h-3 bg-muted rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : !data?.recentActivity.enrollments.length ? (
              <p className="text-muted-foreground text-center py-4">
                No hay inscripciones recientes.
              </p>
            ) : (
              <div className="space-y-4">
                {data.recentActivity.enrollments.map((enrollment) => (
                  <div key={enrollment.id} className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <GraduationCap className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {enrollment.user.name}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">
                        Se inscribio en {enrollment.course.title}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(new Date(enrollment.enrolledAt))}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Payments */}
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">Pagos Recientes</h2>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse flex gap-3">
                    <div className="w-10 h-10 bg-muted rounded-full" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-muted rounded w-3/4" />
                      <div className="h-3 bg-muted rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : !data?.recentActivity.payments.length ? (
              <p className="text-muted-foreground text-center py-4">
                No hay pagos recientes.
              </p>
            ) : (
              <div className="space-y-4">
                {data.recentActivity.payments.map((payment) => (
                  <div key={payment.id} className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-green-500/10 rounded-full flex items-center justify-center">
                      <DollarSign className="h-5 w-5 text-green-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {payment.user.name}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">
                        {payment.course.title}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatCurrency(payment.amount, "USD")} -{" "}
                        {formatDate(new Date(payment.paidAt))}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
